import React, { lazy, Suspense, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { AttachFile } from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import { reportedPeajeDetails } from "./peajePayment";
const ConcessionExcelPreview = lazy(() => import("./ConcessionExcelPreview"));
const desglose = (detail) => {
  if (!detail || typeof detail !== "object") return [];
  if (detail.steps !== undefined) return [{ llave: "", steps: detail.steps, value: detail.value }];
  return Object.entries(detail)
    .filter(([, value]) => value && typeof value === "object")
    .map(([llave, value]) => ({ llave, steps: value.steps, value: value.value }));
};
const colombiaDate = (value) =>
  value && !Number.isNaN(Date.parse(value))
    ? new Intl.DateTimeFormat("es-CO", {
        timeZone: "America/Bogota",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hourCycle: "h23",
      }).format(new Date(value))
    : "Sin fecha registrada";

export default function ConcessionResponse({ response, porPeaje = false }) {
  const [verExcel, setVerExcel] = useState(false);
  const puedeVerExcel = Boolean(response.fileName && /^[1-9]\d*$/.test(String(response.id)));
  return (
    <Stack spacing={2.5}>
      <Alert severity="success">
        {response.persisted
          ? "Respuesta guardada de la concesión."
          : "El envío de esta respuesta fue confirmado."}
      </Alert>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
        {[
          ["Pasos reportados", new Intl.NumberFormat("es-CO").format(response.steps)],
          [
            "Valor reportado",
            new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP" }).format(
              response.value_concil
            ),
          ],
        ].map(([label, value]) => (
          <Box
            key={label}
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.default",
            }}
          >
            <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>{label}</Typography>
            <Typography
              sx={{
                fontSize: 24,
                fontWeight: 700,
                color: "text.primary",
                overflowWrap: "anywhere",
              }}
            >
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
      {response.more && Object.keys(response.more).length > 0 && (
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1.5}
            spacing={1}
          >
            <Typography variant="h6" color="text.primary">
              Valores reportados por peaje
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", whiteSpace: "nowrap" }}>
              {Object.keys(response.more).length} peajes
            </Typography>
          </Stack>
          <Stack spacing={1.5}>
            {Object.entries(response.more).map(([name, detail]) => (
              <Box
                key={name}
                sx={{ border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden" }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: "background.default",
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "text.primary",
                      overflowWrap: "anywhere",
                    }}
                  >
                    {name}
                  </Typography>
                </Box>
                {detail === null && (
                  <Typography sx={{ px: 2, py: 1.5, fontSize: 13, color: "#92400e" }}>
                    Pendiente de reporte
                  </Typography>
                )}
                <Stack
                  divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}
                  spacing={0}
                >
                  {desglose(detail).map((fila, index) => (
                    <Box
                      key={fila.llave || index}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1.4fr 1fr 1fr" },
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                          Llave de conciliación
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 14,
                            color: "text.primary",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {fila.llave || "—"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          borderLeft: { xs: 0, sm: 1 },
                          borderTop: { xs: 1, sm: 0 },
                          borderColor: "divider",
                        }}
                      >
                        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                          Pasos reportados
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: "text.primary",
                            fontVariantNumeric: "tabular-nums",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {Number(fila.steps).toLocaleString("es-CO")}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 2,
                          borderLeft: { xs: 0, sm: 1 },
                          borderTop: { xs: 1, sm: 0 },
                          borderColor: "divider",
                        }}
                      >
                        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}>
                          Valor reportado · COP
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 20,
                            fontWeight: 700,
                            color: "text.primary",
                            fontVariantNumeric: "tabular-nums",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {Number(fila.value).toLocaleString("es-CO", {
                            style: "currency",
                            currency: "COP",
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      )}
      {response.persisted && (
        <Box sx={{ p: 2, bgcolor: "background.default", borderRadius: 2 }}>
          {response.user && (
            <Typography sx={{ fontSize: 13, color: "text.secondary", overflowWrap: "anywhere" }}>
              Enviada por: {response.user}
            </Typography>
          )}
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
            Fecha de envío · Colombia: {colombiaDate(response.createdAt)}
          </Typography>
        </Box>
      )}
      <Box>
        <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
          Observación enviada
        </Typography>
        <Typography
          sx={{
            fontSize: 15,
            color: "text.primary",
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
          }}
        >
          {response.observation}
        </Typography>
      </Box>
      <Box sx={{ p: 2, border: "1px dashed", borderColor: "divider", borderRadius: 2 }}>
        <Typography variant="h6" color="text.primary" sx={{ mb: 1 }}>
          Archivo de soporte
        </Typography>
        {porPeaje ? (
          <Stack spacing={1.5}>
            {(response.files?.length
              ? response.files.map((file) => ({
                  peaje: file.peaje || "Archivo sin peaje asignado",
                  file: {
                    ...file,
                    responseId: file.peaje || response.files.length === 1 ? response.id : undefined,
                    filePeaje: file.peaje || undefined,
                  },
                }))
              : Object.entries(reportedPeajeDetails(response)).map(([peaje, details]) => ({
                  peaje,
                  file: Object.values(details)[0],
                }))
            ).map(({ peaje, file }, index) => {
              return (
                <Box key={`${peaje}-${index}`}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>
                    {peaje}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 13, color: "text.secondary", overflowWrap: "anywhere" }}
                  >
                    {file.fileName || "Sin archivo asociado a este peaje"}
                  </Typography>
                  {/^[1-9]\d*$/.test(String(file.responseId)) && (
                    <MDButton
                      color="info"
                      variant="outlined"
                      aria-label={`Ver Excel de ${peaje}`}
                      onClick={() =>
                        setVerExcel({
                          responseId: file.responseId,
                          fileName: file.fileName,
                          peaje: file.filePeaje,
                        })
                      }
                      sx={{ mt: 1, "&&": { textTransform: "none", fontSize: 13 } }}
                    >
                      Ver Excel
                    </MDButton>
                  )}
                </Box>
              );
            })}
          </Stack>
        ) : (
          <>
            <Stack direction="row" spacing={1} alignItems="center">
              {response.fileName && <AttachFile color="info" />}
              <Typography sx={{ fontSize: 14, color: "text.secondary", overflowWrap: "anywhere" }}>
                {response.fileName || "Se envió sin archivo adjunto."}
              </Typography>
            </Stack>
            {puedeVerExcel && (
              <MDButton
                color="info"
                variant="outlined"
                startIcon={<AttachFile />}
                onClick={() =>
                  setVerExcel({ responseId: response.id, fileName: response.fileName })
                }
                sx={{ mt: 2, "&&": { textTransform: "none", fontSize: 13 } }}
              >
                Ver Excel
              </MDButton>
            )}
            {response.fileName && !puedeVerExcel && (
              <Typography sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>
                Vuelve a consultar la respuesta guardada para ver el archivo adjunto.
              </Typography>
            )}
          </>
        )}
      </Box>
      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
        {response.persisted
          ? "Estos son los datos reportados por la concesión, consultados en el sistema."
          : "Aquí ves los datos de tu último envío en esta visita."}{" "}
        Esta consulta no modifica la conciliación.
      </Typography>
      {verExcel && (
        <Suspense
          fallback={
            <Typography role="status" sx={{ fontSize: 13 }}>
              Abriendo vista previa…
            </Typography>
          }
        >
          <ConcessionExcelPreview
            responseId={verExcel.responseId}
            fileName={verExcel.fileName}
            peaje={verExcel.peaje}
            onClose={() => setVerExcel(false)}
          />
        </Suspense>
      )}
    </Stack>
  );
}
ConcessionResponse.propTypes = {
  porPeaje: PropTypes.bool,
  response: PropTypes.shape({
    steps: PropTypes.number.isRequired,
    value_concil: PropTypes.number.isRequired,
    observation: PropTypes.string.isRequired,
    fileName: PropTypes.string,
    files: PropTypes.arrayOf(
      PropTypes.shape({ fileName: PropTypes.string, peaje: PropTypes.string })
    ),
    more: PropTypes.object,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    persisted: PropTypes.bool,
    user: PropTypes.string,
    createdAt: PropTypes.string,
  }).isRequired,
};
